import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  BarChart3,
  RefreshCw,
  AlertTriangle,
  X,
  MapPin,
  Printer,
  Edit,
  CheckCircle2,
  Layers,
  TrendingUp,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import MainService from '../../services/MainService';
import { getPrintHeaderHtml } from '../../services/printHeaderTemplateService';
import { printHtmlInSameTab } from '../Reports/reportPrintHelpers';

const PRIMARY = '#46955A';
const PRIMARY_LIGHT = '#d1fae5';
const ZERO_BAR = '#e5e7eb';

function LtrChartIsolate({ children, className = '', style }) {
  return (
    <div className={className} style={style} dir="ltr">
      {children}
    </div>
  );
}

const formatCharge = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0.00';
  return num.toFixed(2);
};

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const charge = Number(payload[0]?.value) || 0;
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 shadow-lg">
      <p className="text-xs font-medium text-gray-500">Area</p>
      <p className="text-sm font-semibold text-gray-900">{label}</p>
      <p className="mt-2 text-xs font-medium text-gray-500">Delivery charge</p>
      <p className={`text-base font-bold ${charge > 0 ? 'text-primary' : 'text-gray-400'}`}>
        {formatCharge(charge)}
      </p>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent = 'primary' }) {
  const accents = {
    primary: 'bg-primary/10 text-primary ring-primary/20',
    blue: 'bg-blue-50 text-blue-600 ring-blue-100',
    amber: 'bg-amber-50 text-amber-600 ring-amber-100',
  };
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-500">{label}</p>
          <p className="mt-1 truncate text-2xl font-bold tracking-tight text-gray-900">{value}</p>
        </div>
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ring-1 ${accents[accent] || accents.primary}`}>
          <Icon className="h-5 w-5" strokeWidth={2} />
        </div>
      </div>
    </div>
  );
}

const ClientPriceChart = () => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const resolvedClientId = Number(chartData?.ClientId ?? clientId ?? 0);

  const loadChart = useCallback(async () => {
    if (!clientId) return;
    const requestClientId = Number(clientId);
    try {
      setLoading(true);
      setError('');
      setChartData(null);
      let response = null;
      try {
        response = await MainService.GetClientPriceChart(clientId);
      } catch (chartErr) {
        const [client, charges] = await Promise.all([
          MainService.ClientById(clientId),
          MainService.GetClientPriceCard(clientId),
        ]);
        const items = (Array.isArray(charges) ? charges : [])
          .map((item) => ({
            AreaId: item.AreaId,
            AreaName: item.Area?.Name || '',
            DeliveryCharge: Number(item.DeliveryCharges) || 0,
          }))
          .sort((a, b) => String(a.AreaName).localeCompare(String(b.AreaName)));
        response = {
          ClientId: client?.Id ?? Number(clientId),
          ClientName: client?.Name || '',
          MobileNo1: client?.MobileNo1 || '',
          Items: items,
        };
        if (!client && items.length === 0) throw chartErr;
      }
      if (!response) {
        setError('No price chart data found for this client.');
        setChartData(null);
        return;
      }
      if (Number(response.ClientId) !== requestClientId) {
        setError('Loaded chart data does not match the selected client.');
        setChartData(null);
        return;
      }
      setChartData(response);
    } catch (err) {
      console.error('Failed to load client price chart:', err);
      setError('Failed to load price chart. Please try again.');
      setChartData(null);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadChart();
  }, [loadChart]);

  const clientName = chartData?.ClientName || location.state?.clientName || 'Client';
  const clientMobile = chartData?.MobileNo1 || location.state?.mobileNo || '';

  const tableRows = useMemo(() => {
    const items = Array.isArray(chartData?.Items) ? chartData.Items : [];
    return items
      .map((item) => ({
        areaId: item.AreaId,
        name: item.AreaName || '—',
        charge: Number(item.DeliveryCharge) || 0,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [chartData]);

  const chartRows = useMemo(() => {
    return [...tableRows].sort((a, b) => {
      if (b.charge !== a.charge) return b.charge - a.charge;
      return a.name.localeCompare(b.name);
    });
  }, [tableRows]);

  const chartHeight = Math.max(360, Math.min(960, chartRows.length * 40 + 64));
  const totalAreas = tableRows.length;
  const configuredCount = tableRows.filter((row) => row.charge > 0).length;
  const maxCharge = useMemo(() => Math.max(...tableRows.map((row) => row.charge), 0), [tableRows]);
  const avgCharge = useMemo(() => {
    const configured = tableRows.filter((row) => row.charge > 0);
    if (!configured.length) return 0;
    return configured.reduce((sum, row) => sum + row.charge, 0) / configured.length;
  }, [tableRows]);

  const printPriceChart = async () => {
    if (!tableRows.length) {
      setError('No data to print');
      return;
    }

    try {
      setPrinting(true);
      let printHeaderHtml = '';
      try {
        printHeaderHtml = (await getPrintHeaderHtml()) || '';
      } catch (err) {
        console.warn('Client price chart print: no print header', err);
      }

      const headerBlock = printHeaderHtml
        ? `<div class="company-print-header">${printHeaderHtml}</div>`
        : '';

      const tableRowsHtml = tableRows.map((row) => `
        <tr>
          <td>${escapeHtml(row.name)}</td>
          <td class="price">${escapeHtml(formatCharge(row.charge))}</td>
        </tr>
      `).join('');

      const htmlDoc = `
        <!doctype html>
        <html>
          <head>
            <meta charset="utf-8"/>
            <title>&#x200b;</title>
            <style>
              html, body { margin: 0; padding: 0; }
              body { font-family: Arial, sans-serif; color: #111827; font-size: 12px; }
              .print-inner { box-sizing: border-box; padding: 14mm 16mm 16mm 16mm; }
              .company-print-header { margin-bottom: 14px; }
              .company-print-header img { max-height: 72px; }
              .header { border-bottom: 2px solid #d1d5db; padding-bottom: 14px; margin-bottom: 18px; }
              h1 { margin: 0; font-size: 22px; }
              .meta { margin-top: 8px; color: #4b5563; font-size: 13px; }
              table { width: 100%; border-collapse: collapse; }
              th, td { border: 1px solid #d1d5db; padding: 10px 12px; text-align: left; }
              th { background: #f3f4f6 !important; font-size: 12px; text-transform: uppercase; color: #4b5563; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              td.price { text-align: right; font-weight: 700; }
              @media print {
                @page { size: A4 portrait; margin: 14mm 16mm 16mm 16mm; }
                html, body { margin: 0 !important; padding: 0 !important; }
                .print-inner { padding: 0 !important; }
                thead { display: table-header-group; }
                tr { page-break-inside: avoid; }
              }
            </style>
          </head>
          <body>
            <div class="print-inner">
              ${headerBlock}
              <div class="header">
                <h1>Client Price Chart</h1>
                <div class="meta"><strong>Client:</strong> ${escapeHtml(clientName)}</div>
                <div class="meta"><strong>Mobile:</strong> ${escapeHtml(clientMobile || '-')}</div>
                <div class="meta"><strong>Client ID:</strong> ${escapeHtml(resolvedClientId || clientId || '-')}</div>
                <div class="meta"><strong>Date:</strong> ${escapeHtml(new Date().toLocaleDateString())}</div>
                <div class="meta"><strong>Areas:</strong> ${configuredCount} configured of ${totalAreas} active</div>
              </div>
              <table>
                <thead>
                  <tr><th>Area</th><th style="text-align:right;">Delivery Charge</th></tr>
                </thead>
                <tbody>${tableRowsHtml}</tbody>
              </table>
            </div>
          </body>
        </html>
      `;

      await printHtmlInSameTab(htmlDoc, 'Client price chart print');
    } catch (err) {
      console.error('Client price chart print error:', err);
      setError('Failed to print price chart');
    } finally {
      setPrinting(false);
    }
  };

  const goToManagePrices = () => {
    if (!resolvedClientId) return;
    navigate(`/home/customization/client-area-charges?clientId=${resolvedClientId}`, {
      state: {
        clientId: resolvedClientId,
        clientName: chartData?.ClientName || clientName,
        mobileNo: chartData?.MobileNo1 || clientMobile,
      },
      replace: false,
    });
  };

  const clientInitial = (clientName || 'C').trim().charAt(0).toUpperCase();

  return (
    <div className="space-y-6">
      {error && (
        <div className="fixed top-4 right-4 z-50 flex min-w-[300px] items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 shadow-lg">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError('')} className="flex-shrink-0 text-red-500 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Page header */}
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="bg-gradient-to-r from-primary/10 via-white to-emerald-50/40 px-6 py-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-4">
              <button
                type="button"
                onClick={() => navigate('/home/accounts/clients')}
                className="mt-1 rounded-xl border border-gray-200 bg-white p-2.5 text-gray-600 shadow-sm transition-colors hover:bg-gray-50"
                title="Back to clients"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary text-xl font-bold text-white shadow-md shadow-primary/25">
                  {clientInitial}
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-2xl font-bold text-gray-900">Client Price Chart</h1>
                    <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                      {configuredCount} / {totalAreas} areas
                    </span>
                  </div>
                  <p className="mt-1 text-base font-semibold text-gray-800">{clientName}</p>
                  {clientMobile ? (
                    <p className="mt-0.5 text-sm text-gray-500">{clientMobile}</p>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 lg:justify-end">
              <button
                type="button"
                onClick={goToManagePrices}
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm shadow-primary/20 hover:bg-green-700"
              >
                <Edit className="h-4 w-4" />
                Manage prices
              </button>
              <button
                type="button"
                onClick={printPriceChart}
                disabled={loading || printing || tableRows.length === 0}
                className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-50"
              >
                <Printer className={`h-4 w-4 ${printing ? 'animate-pulse' : ''}`} />
                Print
              </button>
              <button
                type="button"
                onClick={loadChart}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon={CheckCircle2} label="Configured areas" value={loading ? '—' : configuredCount} accent="primary" />
        <StatCard icon={Layers} label="Total active areas" value={loading ? '—' : totalAreas} accent="blue" />
        <StatCard icon={TrendingUp} label="Highest charge" value={loading ? '—' : formatCharge(maxCharge)} accent="amber" />
        <StatCard icon={BarChart3} label="Average charge" value={loading ? '—' : formatCharge(avgCharge)} accent="primary" />
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-gray-200 bg-white py-24 text-gray-500 shadow-sm">
          <RefreshCw className="mb-3 h-9 w-9 animate-spin text-primary" />
          <p className="text-sm font-medium">Loading price chart…</p>
        </div>
      ) : tableRows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 py-24 text-center text-gray-500">
          <MapPin className="mx-auto mb-3 h-10 w-10 text-gray-300" />
          <p className="font-medium">No areas found for this client.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
          {/* Chart panel */}
          <div className="xl:col-span-3">
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white px-5 py-4">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <BarChart3 className="h-5 w-5 text-primary" />
                  Delivery charges by area
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Sorted by charge — highest charges appear at the top.
                </p>
              </div>
              <div className="bg-[linear-gradient(180deg,#f8fafc_0%,#ffffff_120px)] p-4 sm:p-6">
                <div className="overflow-x-auto overflow-y-auto rounded-xl border border-gray-100 bg-white/80 p-3 sm:p-4">
                  <LtrChartIsolate className="min-w-[520px]" style={{ height: chartHeight }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={chartRows}
                        layout="vertical"
                        margin={{ top: 4, right: 56, left: 8, bottom: 4 }}
                        barCategoryGap={12}
                      >
                        <CartesianGrid strokeDasharray="4 4" stroke="#f0f0f0" horizontal={false} />
                        <XAxis
                          type="number"
                          tick={{ fontSize: 11, fill: '#6b7280' }}
                          tickLine={false}
                          axisLine={{ stroke: '#e5e7eb' }}
                          tickFormatter={(v) => formatCharge(v)}
                        />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={160}
                          tickLine={false}
                          axisLine={false}
                          tick={{ fontSize: 12, fill: '#374151', fontWeight: 500 }}
                          interval={0}
                          tickFormatter={(v) => (v.length > 24 ? `${v.slice(0, 24)}…` : v)}
                        />
                        <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(70, 149, 90, 0.06)' }} />
                        <Bar dataKey="charge" radius={[0, 8, 8, 0]} maxBarSize={18}>
                          {chartRows.map((row) => (
                            <Cell
                              key={row.areaId}
                              fill={row.charge > 0 ? PRIMARY : ZERO_BAR}
                            />
                          ))}
                          <LabelList
                            dataKey="charge"
                            position="right"
                            formatter={(v) => formatCharge(v)}
                            style={{ fontSize: 11, fontWeight: 600, fill: '#374151' }}
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </LtrChartIsolate>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-3 w-3 rounded-sm" style={{ background: PRIMARY }} />
                    Configured charge
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-3 w-3 rounded-sm bg-gray-200" />
                    Not configured (0)
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Table panel */}
          <div className="xl:col-span-2">
            <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white px-5 py-4">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <MapPin className="h-5 w-5 text-primary" />
                  Area price list
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Alphabetical list with visual charge indicator.
                </p>
              </div>
              <div className="min-h-0 flex-1 overflow-auto">
                <table className="data-grid-table min-w-full border-separate border-spacing-0">
                  <thead className="sticky top-0 z-10 bg-gray-50 shadow-sm">
                    <tr>
                      <th className="w-10 border-b border-gray-200 px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                        #
                      </th>
                      <th className="border-b border-gray-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Area
                      </th>
                      <th className="border-b border-gray-200 px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Charge
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {tableRows.map((row, index) => {
                      const barWidth = maxCharge > 0 ? Math.round((row.charge / maxCharge) * 100) : 0;
                      const isConfigured = row.charge > 0;
                      return (
                        <tr key={row.areaId} className="group transition-colors hover:bg-primary/[0.03]">
                          <td className="border-b border-gray-100 px-3 py-3 text-center text-xs font-medium text-gray-400">
                            {index + 1}
                          </td>
                          <td className="border-b border-gray-100 px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${
                                isConfigured ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-400'
                              }`}
                              >
                                <MapPin className="h-3.5 w-3.5" />
                              </span>
                              <div className="min-w-0">
                                <p className="truncate text-sm font-medium text-gray-900" title={row.name}>
                                  {row.name}
                                </p>
                                <div className="mt-1.5 h-1.5 w-full max-w-[140px] overflow-hidden rounded-full bg-gray-100">
                                  <div
                                    className="h-full rounded-full transition-all"
                                    style={{
                                      width: `${isConfigured ? Math.max(barWidth, 6) : 0}%`,
                                      background: isConfigured ? PRIMARY : 'transparent',
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="border-b border-gray-100 px-4 py-3 text-right">
                            <span className={`inline-flex min-w-[4.5rem] items-center justify-end rounded-lg px-2.5 py-1 text-sm font-bold tabular-nums ${
                              isConfigured
                                ? 'bg-primary/10 text-primary ring-1 ring-primary/20'
                                : 'bg-gray-50 text-gray-400 ring-1 ring-gray-100'
                            }`}
                            >
                              {formatCharge(row.charge)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="border-t border-gray-100 bg-gray-50 px-5 py-3 text-xs text-gray-500">
                {configuredCount} configured · {totalAreas - configuredCount} at zero
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPriceChart;
